import { useEffect } from "react";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../shared-components/ConfirmationDialog";
import { useCreateItemPostRating, useRemovePostsItem } from "../api/ItemsApi";
import { ItemWithMetaData } from "../api/Items";
import SystemMediaPostHeader from "./SystemMediaPostHeader";
import { contentRatingsAndImagesUrlFromPost } from "./helpers";
import { SystemEditMediaModalActions } from "./SystemEditMediaModal";
import useSystemEditMediaModal from "./SystemEditMediaModal";
import { HARDCODED_ACTOR_UUID } from "../api/helpers";
import RatingBadges from "../shared-components/RatingBadges";
import {
  PostImage,
  contentMediaToMediaExternalInternal,
} from "../posts/PostImage";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { atLeastOneItem } from "../utils";

type PostRatingBehavior = {
  name: string;
  score_type: "behavior";
};

type SocialMediaPostProps = {
  behaviors: string[];
  post: ItemWithMetaData;
  isReadOnly: boolean;
  loadPosts: (noPostsMessage: boolean) => void;
};

export const SystemPost = ({
  behaviors,
  post,
  isReadOnly,
  loadPosts,
}: SocialMediaPostProps) => {
  const sortedBehaviors = behaviors.sort();
  const {
    data: removeData,
    mutate: removeItem,
    isLoading: isLoadingRemoveItem,
  } = useRemovePostsItem();

  const { modal, show, hide } = useConfirmationDialog();

  const { postContent, ratings } = contentRatingsAndImagesUrlFromPost(post);
  const {
    data: itemRatingData,
    mutate: createItemRating,
    isLoading: isLoadingItemRating,
  } = useCreateItemPostRating();
  useEffect(() => {
    if (removeData) {
      loadPosts(false);
    }
    // eslint-disable-next-line
  }, [removeData]);

  useEffect(() => {
    if (itemRatingData && !isLoadingItemRating) {
      loadPosts(false);
    }
    // eslint-disable-next-line
  }, [itemRatingData, isLoadingItemRating]);

  const { modal: systemEditModal, show: showSystemEditModal } =
    useSystemEditMediaModal(editActionHandler);

  const handleOnRemove = () => {
    show(
      "",
      "Are you sure you want to remove post?",
      (action: ConfirmDialogActions) => {
        if (action === "continue") {
          removeItem({
            item_uuid: post.item.external_id,
            modified_by: HARDCODED_ACTOR_UUID, // TODO: remove once the actor uuid definition is complete
          });
          hide();
        }
      }
    );
  };

  function editActionHandler(
    action: SystemEditMediaModalActions,
    rating: PostRatingBehavior[]
  ) {
    if (action === "save") {
      createItemRating({
        item_uuid: post.item.external_id,
        ratings: rating,
      });
    }
  }

  function handleOnEdit() {
    showSystemEditModal(
      post,
      sortedBehaviors,
      (
        action: SystemEditMediaModalActions,
        ratingsSelections: PostRatingBehavior[]
      ) => {
        if (action === "save") {
          createItemRating({
            item_uuid: post.item.external_id,
            ratings: ratingsSelections,
          });
        }
      }
    );
  }
  return (
    <div className="border p-4 rounded-lg  mb-4">
      <SystemMediaPostHeader
        postUrl={postContent.url}
        profileUrl={postContent.profile_url}
        datePosted={postContent.created_at}
        isReadOnly={isReadOnly}
        handleOnRemove={handleOnRemove}
        handleOnEdit={handleOnEdit}
        hideRemove={
          (post.keywords?.length ?? 0) > 0 && post.rating?.length === 0
        }
      />

      <div className="h-[1px] w-full bg-gray-300 mb-4"></div>
      <div className="flex flex-nowrap mt-3 overflow-clip">
        <div className="max-w-80">
          {postContent?.media?.length > 0 && (
            <PostImage
              media={contentMediaToMediaExternalInternal(postContent)}
            />
          )}
        </div>
        <div className="flex flex-nowrap flex-col">
          <div className=" align-top mx-3">{postContent.text}</div>
          <div id="tags" className="flex flex-col p-3 gap-3">
            {atLeastOneItem(ratings) && (
              <RatingBadges ratings={ratings} showFlagIcon={false} />
            )}
          </div>
        </div>
      </div>
      {modal}
      {systemEditModal}
      {(isLoadingItemRating || isLoadingRemoveItem) && <LoadingSpinner />}
    </div>
  );
};
